import { useApolloClient } from "@apollo/client/react";
import NumberFlow from "@number-flow/react";
import type { ReactNode } from "react";
import { useContext, useRef } from "react";
import { LuGem } from "react-icons/lu";

import { DiamondSlider } from "@/components/shared/diamond-slider";
import { FeedContext } from "@/contexts/feed-context";
import { usePostContext } from "@/contexts/post-context";
import { UserContext } from "@/contexts/user-context";
import type {
  PostAssociation,
  PostByPostHashStatsQuery,
} from "@/graphql/codegen/graphql";
import { useGetCurrentUserPostStats } from "@/hooks-api/use-get-current-user-post-stats";
import { AssociationReactionValue, AssociationType } from "@/utils/constants";
import { cn } from "@/utils/lib";

type PostDiamondsProps = {
  triggerClassName?: string;
  creatorPublicKey: string;
  customHoverTrigger?: (hasCurrentUserSentDiamondsToPost: boolean) => ReactNode;
};

export const PostDiamonds = ({
  triggerClassName = "text-base",
  creatorPublicKey,
  customHoverTrigger,
}: PostDiamondsProps) => {
  const { user, refetchUser } = useContext(UserContext);
  const { post, isPureRepost, repost, updatePost } = usePostContext();
  const postHash = isPureRepost ? repost?.postHash : post.postHash;
  const finalPost = isPureRepost ? repost : post;
  const apolloClient = useApolloClient();
  const { posts, updatePostByHash } = useContext(FeedContext);

  if (!postHash) {
    throw new Error("Post hash is required");
  }

  const {
    data,
    readLatestData,
    setOptimistic,
    diamondsTotalCount,
    diamondsTotalUsd,
    hasCurrentUserDiamonds,
  } = useGetCurrentUserPostStats(isPureRepost ? repost : post);

  const revertFnRef = useRef<() => void>(null!);

  const hoverTrigger = customHoverTrigger ? (
    customHoverTrigger(hasCurrentUserDiamonds)
  ) : (
    <div
      className={cn(
        `
          flex cursor-pointer items-center gap-2 text-muted
          hover:text-primary
        `,
        triggerClassName,
        {
          "text-primary": hasCurrentUserDiamonds,
        },
      )}
    >
      <LuGem className="flex" />
      <div className="flex cursor-pointer items-center gap-2">
        <span
          className={cn(
            `
              hidden text-xs
              sm:inline
            `,
            {
              hidden: diamondsTotalCount === 0,
            },
          )}
        >
          <NumberFlow
            value={diamondsTotalCount!}
            format={{
              notation: "compact",
            }}
          />
        </span>

        <span
          className={cn("relative -top-px font-mono text-xs", {
            hidden: diamondsTotalUsd === 0,
          })}
        >
          <span
            className="
              hidden
              sm:inline
            "
          >
            (
          </span>
          <NumberFlow
            value={diamondsTotalUsd}
            format={{
              style: "currency",
              currency: "USD",
              currencyDisplay: "narrowSymbol",
              maximumFractionDigits:
                diamondsTotalUsd > 100 || diamondsTotalUsd < -100 ? 0 : 2,
            }}
          />
          <span
            className="
              hidden
              sm:inline
            "
          >
            )
          </span>
        </span>
      </div>
    </div>
  );

  return creatorPublicKey !== user?.publicKey ? (
    <DiamondSlider
      {...{
        params: {
          postHash,
        },
        creatorPublicKey,
        onDiamondSend: ({ valueUsdCents }) => {
          const baseCurrentUserStats =
            readLatestData()?.postByPostHash ??
            data?.postByPostHash ??
            ({
              __typename: "Post",
              postHash,
              currentUserReposts: {
                __typename: "PostsConnection",
                nodes: [],
              },
              currentUserQuotes: {
                __typename: "PostsConnection",
                nodes: [],
              },
              currentUserDiamonds: {
                __typename: "PostAssociationsConnection",
                nodes: [],
              },
              currentUserReactions: {
                __typename: "PostAssociationsConnection",
                nodes: [],
              },
            } as NonNullable<PostByPostHashStatsQuery["postByPostHash"]>);

          const newCurrentUserStats = structuredClone(
            baseCurrentUserStats,
          ) as NonNullable<PostByPostHashStatsQuery["postByPostHash"]>;
          const newPostStats = structuredClone(
            finalPost?.postStats ?? post.postStats ?? {},
          );
          const optimisticReactionTransactor =
            user && user.id
              ? {
                  __typename: "Account" as const,
                  id: user.id,
                  publicKey: user.publicKey,
                  username: user.username,
                  extraData: user.extraData,
                  description: user.description,
                  pkid: user.pkid,
                  isVerified: user.isVerified,
                  creatorBasisPoints: user.creatorBasisPoints,
                  totalBalanceUsdCents: user.totalBalanceUsdCents,
                  daoCoinMintingDisabled: user.daoCoinMintingDisabled,
                  daoCoinsInCirculationNanosHex:
                    user.daoCoinsInCirculationNanosHex,
                  isBlacklisted: user.isBlacklisted,
                  subscriptionTiers: user.subscriptionTiers ?? {
                    __typename: "SubscriptionTiersConnection" as const,
                    totalCount: 0,
                    nodes: [],
                  },
                  isGreylisted: user.isGreylisted ?? {
                    __typename: "UserAssociationsConnection" as const,
                    totalCount: 0,
                  },
                  isNSFW: user.isNSFW ?? {
                    __typename: "UserAssociationsConnection" as const,
                    totalCount: 0,
                  },
                  isVerifier: user.isVerifier ?? {
                    __typename: "UserAssociationsConnection" as const,
                    totalCount: 0,
                  },
                }
              : null;
          const currentUserDiamondNodes = [
            ...(newCurrentUserStats.currentUserDiamonds?.nodes ?? []),
          ];
          const currentUserReactionNodes = [
            ...(newCurrentUserStats.currentUserReactions?.nodes ?? []),
          ];

          newPostStats.diamondsTotalCount ||= 0;
          newPostStats.diamondsTotalCount += 1;

          newPostStats.diamondsTotalUsdCents ||= 0;
          newPostStats.diamondsTotalUsdCents += valueUsdCents;

          newCurrentUserStats.currentUserDiamonds = {
            __typename:
              newCurrentUserStats.currentUserDiamonds?.__typename ??
              "PostAssociationsConnection",
            nodes: currentUserDiamondNodes,
          };
          currentUserDiamondNodes.push({
            badgerKey: "1",
            associationId: "1",
          });

          // Only auto-upvote if the user hasn't already reacted
          if (
            !currentUserReactionNodes.find(
              (association) =>
                association?.associationValue === AssociationReactionValue.LIKE,
            )
          ) {
            newCurrentUserStats.currentUserReactions = {
              __typename: "PostAssociationsConnection",
              nodes: currentUserReactionNodes,
            };

            currentUserReactionNodes.push({
              __typename: "PostAssociation",
              associationType: AssociationType.reaction,
              associationValue: AssociationReactionValue.LIKE,
              associationId: crypto.randomUUID(),
              transactor:
                optimisticReactionTransactor as unknown as PostAssociation["transactor"],
            } as NonNullable<(typeof currentUserReactionNodes)[number]>);

            newPostStats.totalReactionCount ||= 0;
            newPostStats.totalReactionCount += 1;
          }

          revertFnRef.current = setOptimistic({
            postByPostHash: newCurrentUserStats,
          });

          // Optimistically update the post itself in the cache as it's a separate entity
          const applyOptimisticPostStats = (targetPost: typeof post) => {
            if (targetPost.postHash === postHash) {
              return {
                ...targetPost,
                postStats: newPostStats,
              };
            }

            if (targetPost.repostedPost?.postHash === postHash) {
              return {
                ...targetPost,
                repostedPost: {
                  ...targetPost.repostedPost,
                  postStats: newPostStats,
                },
              };
            }

            return targetPost;
          };

          apolloClient.cache.modify({
            id: apolloClient.cache.identify(finalPost ?? post),
            fields: {
              postStats: () => {
                return newPostStats;
              },
            },
          });

          // Posts in the feed are stored in cache in custom state, it's hacky, but we need to update them manually
          const feedPost = posts.find(
            (p) =>
              p.postHash === postHash || p.repostedPost?.postHash === postHash,
          );
          if (feedPost?.postHash) {
            updatePostByHash(
              feedPost.postHash,
              applyOptimisticPostStats(feedPost),
            );
          }

          if (postHash) {
            updatePost(postHash, applyOptimisticPostStats(post));
          }
        },
        onDiamondSent: () => {
          refetchUser();
        },
        onDiamondError: (...args) => {
          // Don't pass the ref directly
          revertFnRef.current?.(...args);
        },
        hoverTrigger,
      }}
    />
  ) : (
    hoverTrigger
  );
};
